<template>
  <layout-basic>
    <page-hero
      title="IMPROVE Launch Programme"
      lead="Get your organisation live quickly with guided onboarding from our Success Team — a one-off investment of £695."
    />

    <section class="launch-intro">
      <v-container>
        <div class="launch-intro__copy">
          <p>
            The IMPROVE Launch Programme is how we set every new customer up for
            success. After you
            <router-link to="/get-started">buy IMPROVE</router-link>, our Success
            Team helps you configure the platform, load your users and run a live
            Launch session so your people are ready from day one. Prefer to explore
            first?
            <router-link to="/experience">Experience IMPROVE</router-link>.
          </p>
        </div>
      </v-container>
    </section>

    <point-grid
      title="What happens"
      :points="steps"
      :columns="2"
      tint
    />

    <section class="launch-actions">
      <v-container>
        <h2 class="font-sora launch-actions__title">Ready to go live?</h2>
        <p class="launch-actions__lead">
          Choose your package online, or talk to us if you’d like help first.
        </p>
        <div class="launch-actions__btns">
          <router-link to="/get-started" class="launch-actions__btn launch-actions__btn--primary">
            Buy IMPROVE
          </router-link>
          <router-link to="/experience" class="launch-actions__btn launch-actions__btn--secondary">
            Experience IMPROVE
          </router-link>
          <a href="/contact" class="launch-actions__btn launch-actions__btn--secondary">
            Contact
          </a>
        </div>
      </v-container>
    </section>

    <section class="launch-booking">
      <v-container style="max-width: 960px">
        <embed-frame
          :src="bookingUrl"
          title="Book your Launch session"
          subtitle="Schedule your Microsoft Teams Launch with the IMPROVE Success Team."
          iframe-title="IMPROVE Launch session booking"
          placeholder-title="Booking opens after you purchase"
          placeholder-body="After you buy IMPROVE, you can book your Teams Launch session here. Until booking is available, contact Success and we’ll help you schedule — or head to Buy IMPROVE to purchase first."
          fallback-href="/get-started"
          fallback-label="Buy IMPROVE"
        />
        <p class="launch-booking__note">
          Ideally book after payment via
          <router-link to="/get-started">Buy IMPROVE</router-link>.
          Our finance team confirms payment before your session — this keeps
          everything smooth for you and Success.
        </p>
      </v-container>
    </section>

    <standard-cta />
  </layout-basic>
</template>

<script>
import LayoutBasic from '@/layouts/Dashboard';
import PageHero from '@/components/marketing/PageHero.vue';
import PointGrid from '@/components/marketing/PointGrid.vue';
import StandardCta from '@/components/marketing/StandardCta.vue';
import EmbedFrame from '@/components/marketing/EmbedFrame.vue';
import { marketingPageMeta } from '@/utils/marketingPage';
import { EMBEDS } from '@/config/embeds';

export default {
  name: 'LaunchProgramme',
  components: {
    LayoutBasic,
    PageHero,
    PointGrid,
    StandardCta,
    EmbedFrame,
  },
  metaInfo() {
    return marketingPageMeta({
      title: 'IMPROVE Launch Programme | IMPROVE',
      description:
        'Get your organisation live quickly with guided onboarding from our Success Team. One-off Launch Programme fee of £695.',
      path: '/launch-programme',
    });
  },
  data() {
    return {
      bookingUrl: EMBEDS.launchBookingUrl,
      steps: [
        'Welcome & setup guidance from the Success Team',
        'Share the onboarding information we need to configure your platform',
        'We load your users and prepare IMPROVE for launch',
        'Book your IMPROVE Launch session via Teams',
        'Go live with confidence and start training your people',
        'Ongoing support whenever you need it',
      ],
    };
  },
};
</script>

<style scoped>
.launch-intro {
  padding: 8px 0 16px;
}

.launch-intro__copy {
  max-width: 720px;
  margin: 0 auto;
  color: var(--brand-navy, #002458);
  font-size: 17px;
  line-height: 1.6;
  text-align: center;
}

.launch-intro__copy p {
  margin: 0;
}

.launch-intro__copy a {
  color: var(--brand-teal, #08a0a8);
  font-weight: 600;
  text-decoration: none;
}

.launch-intro__copy a:hover {
  text-decoration: underline;
}

.launch-actions {
  padding: 40px 0 24px;
  text-align: center;
}

.launch-actions__title {
  color: var(--brand-navy, #002458);
  font-size: 28px;
  font-weight: 700;
  margin: 0 0 10px;
}

.launch-actions__lead {
  color: var(--brand-grey, #6b7280);
  margin: 0 0 20px;
  font-size: 16px;
}

.launch-actions__btns {
  display: flex;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;
}

.launch-actions__btn {
  display: inline-block;
  padding: 14px 28px;
  border-radius: 6px;
  font-weight: 700;
  text-decoration: none;
  font-size: 16px;
}

.launch-actions__btn--primary {
  background: var(--brand-navy, #002458);
  color: #fff;
}

.launch-actions__btn--primary:hover {
  background: var(--brand-teal, #08a0a8);
  color: #fff;
}

.launch-actions__btn--secondary {
  background: #fff;
  color: var(--brand-navy, #002458);
  border: 2px solid var(--brand-navy, #002458);
}

.launch-actions__btn--secondary:hover {
  border-color: var(--brand-teal, #08a0a8);
  color: var(--brand-teal, #08a0a8);
}

.launch-booking {
  padding: 24px 0 40px;
}

.launch-booking__note {
  margin: 16px auto 0;
  max-width: 640px;
  text-align: center;
  color: var(--brand-grey, #6b7280);
  font-size: 14px;
  line-height: 1.5;
}

.launch-booking__note a {
  color: var(--brand-teal, #08a0a8);
  font-weight: 600;
  text-decoration: none;
}

.launch-booking__note a:hover {
  text-decoration: underline;
}
</style>
